import React, { useCallback, useEffect, useRef, useState } from 'react';
import DraggableImport, { type DraggableProps } from 'react-draggable';
import { Plus, X } from 'lucide-react';
import { Logo } from '@upsales/components';
import { useSelf } from 'App/components/hooks/appHooks';
import ShadcnScope from 'App/shadcn/components/ShadcnScope';
import { TooltipProvider } from 'App/shadcn/ui/tooltip';
import WorkspaceChat from 'App/shadcn/pages/AIWorkspace/Chat/WorkspaceChat';
import WorkspaceInbox from 'App/shadcn/pages/AIWorkspace/Chat/WorkspaceInbox';
import { useInboxCount, inboxCountStore } from './useInboxCount';
import workspaceStyles from 'App/shadcn/pages/AIWorkspace/workspaceStyles';
import praxisStyles from 'App/shadcn/pages/Assistant/praxisStyles';
import embedStyles from 'App/pages/EmbedAIChat/embedStyles';
import { agentColor } from 'App/shadcn/pages/AIWorkspace/components/AgentChip';
import { usePersonalAssistantIdentity } from 'App/shadcn/pages/AIWorkspace/Chat/usePersonalAssistantIdentity';
import AssistantResource from 'Resources/AssistantChat';
import type { Conversation } from 'App/shadcn/pages/Assistant/types';
import { notifyAIWorkspaceError } from 'App/shadcn/pages/AIWorkspace/notifyAIWorkspaceError';
import {
	getResumableConversationId,
	setLastConversationId,
	touchLastConversation
} from 'App/shadcn/pages/Assistant/assistantStorage';
import T from 'Components/Helpers/translate';
import { useChatOpen, chatOpenStore } from './useChatOpen';
import { useCurrentPageContext } from './useCurrentPageContext';
import { useFloatingChatAccess } from './useFloatingChatAccess';
import { useFloatingChatWindow } from './useFloatingChatWindow';
import floatingChatStyles from './floatingChatStyles';
import MeetingContextPill from './MeetingContextPill';
import { SNAP_MS, type Corner } from './floatingChatGeometry';

// react-draggable@4 declares `static defaultProps: DraggableProps` (non-partial), which
// React 17's LibraryManagedAttributes turns into required JSX props. Re-type to Partial.
const Draggable = DraggableImport as unknown as React.ComponentClass<Partial<DraggableProps>>;

// Corner resize handles. They sit on the light-DOM wrapper (outside ShadcnScope's shadow root),
// so floatingChatStyles can't reach them — they're styled inline, like the wrapper itself.
// The top-right corner is omitted on purpose: the header's tab/close buttons live there, and a
// handle painted above them would swallow their clicks.
const CORNERS: Corner[] = ['nw', 'sw', 'se'];
const handleStyle = (corner: Corner): React.CSSProperties => ({
	position: 'absolute',
	width: 16,
	height: 16,
	zIndex: 20,
	touchAction: 'none',
	cursor: corner === 'sw' ? 'nesw-resize' : 'nwse-resize',
	top: corner[0] === 'n' ? 0 : undefined,
	bottom: corner[0] === 's' ? 0 : undefined,
	left: corner[1] === 'w' ? 0 : undefined,
	right: corner[1] === 'e' ? 0 : undefined
});
// Styles are static per corner — build them once, not on every render.
const RESIZE_HANDLES = CORNERS.map(corner => ({ corner, style: handleStyle(corner) }));

type Tab = 'chat' | 'inbox' | 'conversations';

const FloatingAIChatWindow: React.FC<{ selfId?: number }> = ({ selfId }) => {
	const { assistantName, avatarUrl, personalAgentId } = usePersonalAssistantIdentity(selfId);
	const trustedContext = useCurrentPageContext();
	// WEB-10278: reopening the window within the resume window puts you back in
	// the chat you were reading; outside it you get a new chat as before. The
	// window only mounts while open, so this initialiser runs on every reopen.
	const [conversationId, setConversationId] = useState<number | null>(
		() => chatOpenStore.requestedConversationId() ?? getResumableConversationId()
	);
	// Bumped to remount WorkspaceChat for a fresh chat (mirrors AIWorkspace's chatKey).
	const [chatKey, setChatKey] = useState(0);
	// The first message a requested conversation opens with; WorkspaceChat sends it once.
	const [pendingMessage, setPendingMessage] = useState<string | null>(() => chatOpenStore.requestedMessage());
	// Or what its composer opens with, for the user to edit and send.
	const [pendingDraft, setPendingDraft] = useState<string | null>(() => chatOpenStore.requestedDraft());
	// Who a new chat is with; null is the personal assistant.
	const [chatAgentId, setChatAgentId] = useState<number | null>(null);
	const [activeTab, setActiveTab] = useState<Tab>('chat');
	const [conversations, setConversations] = useState<Conversation[]>([]);
	const inboxCount = useInboxCount();
	const nodeRef = useRef<HTMLDivElement>(null);

	const { pos, size, snapping, bounds, onDragStart, onDrag, onDragStop, resizeHandleProps } = useFloatingChatWindow();

	const loadConversations = useCallback(() => {
		AssistantResource.listConversations()
			.then(list =>
				setConversations(
					list
						.filter(
							c =>
								!c.contextType ||
								c.contextType === 'general' ||
								c.contextType === 'slack' ||
								c.contextType === 'appointment' ||
								(c.contextType === 'agent' && c.parentConversationId == null)
						)
						.sort((a, b) => new Date(b.modDate).getTime() - new Date(a.modDate).getTime())
				)
			)
			.catch(err => notifyAIWorkspaceError(err, 'listConversations'));
	}, []);

	const showConversations = useCallback(() => {
		setActiveTab('conversations');
		loadConversations();
	}, [loadConversations]);

	const handleSelect = useCallback((conv: Conversation) => {
		setPendingMessage(null);
		setPendingDraft(null);
		setLastConversationId(conv.id);
		setConversationId(conv.id);
		setChatAgentId(null);
		setActiveTab('chat');
	}, []);

	// A request that opened the window was taken by the state initialiser above; one made while
	// the window is already open arrives through the subscription.
	useEffect(() => {
		const initial = chatOpenStore.requestedConversationId();
		if (initial !== null) {
			setLastConversationId(initial);
			chatOpenStore.clearRequestedConversation();
		}
		return chatOpenStore.subscribe(() => {
			const requested = chatOpenStore.requestedConversationId();
			if (requested === null) return;
			const message = chatOpenStore.requestedMessage();
			const draft = chatOpenStore.requestedDraft();
			chatOpenStore.clearRequestedConversation();
			setLastConversationId(requested);
			setConversationId(requested);
			setChatAgentId(null);
			setActiveTab('chat');
			// Every request is a new chat. WorkspaceChat sends its initialMessage, and its composer
			// reads its draft, once per mount, so it remounts even with neither - otherwise an unsent
			// draft from the last request stays in the composer.
			setPendingMessage(message);
			setPendingDraft(draft);
			setChatKey(k => k + 1);
		});
	}, []);

	const clearPendingMessage = useCallback(() => setPendingMessage(null), []);

	const handleNewChat = useCallback(() => {
		setPendingMessage(null);
		setPendingDraft(null);
		setLastConversationId(null);
		setConversationId(null);
		setChatAgentId(null);
		setChatKey(k => k + 1);
		setActiveTab('chat');
	}, []);

	// Esc closes the chat. This component only mounts while open, so attach on mount.
	// Skip if a nested control (popover, dropdown) already handled the Esc.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && !e.defaultPrevented) chatOpenStore.close();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	// While the window is open the background poll is paused — the window drives its own inbox
	// refreshes below. On close (unmount) the poll resumes with an immediate refresh.
	useEffect(() => {
		inboxCountStore.setActive(false);
		return () => inboxCountStore.setActive(true);
	}, []);

	// Refresh the badge count when the open conversation changes (reading a thread drops its unread count).
	useEffect(() => {
		inboxCountStore.refresh();
	}, [conversationId]);

	// Keep the resume window measuring time since the chat was last on screen:
	// restart it when the tab is hidden and when the window is closed.
	useEffect(() => {
		if (conversationId == null) return;
		document.addEventListener('visibilitychange', touchLastConversation);
		return () => {
			document.removeEventListener('visibilitychange', touchLastConversation);
			touchLastConversation();
		};
	}, [conversationId]);

	return (
		<Draggable
			nodeRef={nodeRef}
			handle=".floating-chat-head"
			position={pos}
			onStart={onDragStart}
			onDrag={onDrag}
			onStop={onDragStop}
			bounds={bounds}
		>
			<div
				ref={nodeRef}
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: size.width,
					height: size.height,
					zIndex: 100000,
					// Round + clip + shadow live HERE, on the element react-draggable transforms.
					// A rounded overflow clip on a descendant of a transformed element is
					// mis-composited by Chromium (leaks square corners); keeping the clip on the
					// transformed element itself is exact. Hex literals, not --ws-* vars: this div
					// is in the light DOM, outside the shadow root where those vars are defined.
					// clip, not hidden — hidden is still programmatically scrollable, and Radix's
					// focus on the opening delegation sheet (slides in from off-window) shifted it.
					borderRadius: 12,
					overflow: 'clip',
					boxShadow: '0 0 0 1px #e4eaf0, 0 12px 40px rgba(0, 0, 0, 0.18)',
					// Only while docking — kept off during live drag so dragging never lags.
					transition: snapping ? `transform ${SNAP_MS}ms ease` : undefined
				}}
			>
				<ShadcnScope style={{ width: '100%', height: '100%' }}>
					<TooltipProvider delayDuration={200}>
						<style>{workspaceStyles}</style>
						<style>{praxisStyles}</style>
						<style>{embedStyles}</style>
						<style>{floatingChatStyles}</style>
						<div className="floating-chat">
							<div className="floating-chat-head">
								<div className="floating-chat-tabs">
									<button
										type="button"
										className={`floating-chat-tab${activeTab === 'chat' ? ' is-active' : ''}`}
										onClick={() => setActiveTab('chat')}
									>
										{T('assistant.chat')}
									</button>
									<button
										type="button"
										className={`floating-chat-tab${activeTab === 'inbox' ? ' is-active' : ''}`}
										onClick={() => setActiveTab('inbox')}
									>
										{T('assistant.inbox')}
										{inboxCount > 0 && (
											<span className="floating-chat-tab-count">
												{inboxCount > 9 ? '9+' : inboxCount}
											</span>
										)}
									</button>
									<button
										type="button"
										className={`floating-chat-tab${
											activeTab === 'conversations' ? ' is-active' : ''
										}`}
										onClick={showConversations}
									>
										{T('assistant.conversations')}
									</button>
									<button
										type="button"
										className="floating-chat-tab floating-chat-tab--action"
										onClick={handleNewChat}
									>
										<Plus />
										{T('assistant.newChat')}
									</button>
								</div>
								<button
									type="button"
									className="embed-icon-btn"
									title={T('default.close')}
									aria-label={T('default.close')}
									onClick={() => chatOpenStore.close()}
								>
									<X />
								</button>
							</div>
							<div className="floating-chat-body">
								<div className="ai-ws ai-ws-embed">
									<main className="ws-main">
										<div className={`ws-main-inner${activeTab !== 'chat' ? ' is-hidden' : ''}`}>
											<MeetingContextPill conversationId={conversationId} />
											{conversationId === null && (
												<div className="embed-empty">
													<div className="embed-empty-logo">
														<Logo color="green" />
													</div>
													<div className="embed-empty-tagline">
														{T('assistant.embedTagline')}
													</div>
												</div>
											)}
											<WorkspaceChat
												key={chatKey}
												assistantName={assistantName}
												avatarUrl={avatarUrl}
												avatarColor={personalAgentId ? agentColor(personalAgentId) : undefined}
												personalAgentId={personalAgentId}
												conversationId={conversationId}
												setConversationId={setConversationId}
												selfId={selfId}
												contextType={chatAgentId ? 'agent' : 'general'}
												contextId={chatAgentId ?? undefined}
												onSelectAgent={setChatAgentId}
												trustedContext={trustedContext}
												hideTopbar
												hideWelcome
												initialMessage={pendingMessage}
												onInitialMessageSent={clearPendingMessage}
												initialDraft={pendingDraft}
											/>
										</div>
										{activeTab === 'inbox' && (
											<div className="floating-chat-pane">
												<WorkspaceInbox
													currentConversationId={conversationId}
													onSelectConversation={id => {
														setConversationId(id);
														setChatAgentId(null);
														setActiveTab('chat');
													}}
												/>
											</div>
										)}
										{activeTab === 'conversations' && (
											<div className="floating-chat-pane">
												<div className="embed-conv-list">
													{conversations.map(conv => (
														<button
															key={conv.id}
															type="button"
															className={`embed-conv-item${
																conv.id === conversationId ? ' is-active' : ''
															}`}
															onClick={() => handleSelect(conv)}
														>
															{conv.title || T('assistant.newChat')}
														</button>
													))}
												</div>
											</div>
										)}
									</main>
								</div>
							</div>
						</div>
					</TooltipProvider>
				</ShadcnScope>
				{RESIZE_HANDLES.map(({ corner, style }) => (
					<div key={corner} aria-hidden="true" style={style} {...resizeHandleProps(corner)} />
				))}
			</div>
		</Draggable>
	);
};

// Outer gate: only the cheap hooks. The window body — including the identity hook that
// fetches the user's personal agent and the page-context listener — must NOT run until the
// chat is actually shown. Mounting it globally and running those hooks on every render would
// fire network calls on every page load (and breaks the jsdom smoke tests, where there's no
// fetch). Rendering the inner component only when open + access gates that work.
const FloatingAIChat: React.FC = () => {
	const open = useChatOpen();
	const self = useSelf();
	const hasAccess = useFloatingChatAccess();

	if (!open || !hasAccess) return null;
	return <FloatingAIChatWindow selfId={self?.id} />;
};

export default FloatingAIChat;
